export function sanitizeABN(abn: string | number): string {
  return String(abn).replace(/\D/g, '')
}

// ABN Validation format: https://abr.business.gov.au/Help/AbnFormat
export function validateABN(abn: string | number) {
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19]
  const abnString = sanitizeABN(abn)

  if (abnString.length !== 11) {
    return false
  }

  const sum = weights.reduce((acc, weight, index) => {
    const digit = Number(abnString[index]) - (index === 0 ? 1 : 0)
    return acc + weight * digit
  }, 0)

  const isValid = (sum % 89) === 0
  return isValid
}

// The NZBN is a GLN GTIN-13 (same standards used to create bar codes)
// https://www.gs1.org/services/how-calculate-check-digit-manually
export function validateNZBN(nzbn: string): boolean {
  if (nzbn.toString().length !== 13) {
    return false
  }

  let sum = 0
  for (let i = 0; i < 12; i++) {
    // Multiply by 1 if it's an even index, else multiply by 3
    sum += (i % 2 === 0 ? 1 : 3) * parseInt(nzbn[i])
  }

  const checkDigit = (10 - (sum % 10)) % 10
  const valid = checkDigit === parseInt(nzbn[12])
  return valid
}

// A CRN is a unique identifier for UK companies.
// It is 8 characters long: 8 digits or 2 letters from a list of combinations followed by 6 digits
// https://www.yourcompanyformations.co.uk/blog/company-registration-number-crn-number-explained/
const CRN_VALIDATION_REGEX = /^(((AC|CE|CS|FC|FE|GE|GS|IC|LP|NC|NF|NI|NL|NO|NP|OC|OE|PC|R0|RC|SA|SC|SE|SF|SG|SI|SL|SO|SR|SZ|ZC|\d{2})\d{6})|((IP|SP|RS)[A-Z\d]{6})|(SL\d{5}[\dA]))$/

export function validateCRN(crn: string): boolean {
  return CRN_VALIDATION_REGEX.test(crn)
}

export function validateEIN(ein: string): boolean {
  const validationRegex = /^[0-9]{9}$/
  return validationRegex.test(ein)
}

// A CRA Business Number is 9 digits (BN9). A program account (BN15) is the BN9
// plus a 2-letter program identifier and a 4-digit reference, e.g. 123456789RT0001.
// https://www.canada.ca/en/revenue-agency/services/forms-publications/publications/rc2/rc2-business-number-your-canada-revenue-agency-program-accounts.html
// https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/business-registration/business-number-program-account/need-program-accounts.html
const CRA_PROGRAM_IDENTIFIERS = 'RR|RZ|RP|RC|RT'
const CRA_PROGRAM_IDENTIFIERS_INPUT = '[Rr][Rr]|[Rr][Zz]|[Rr][Pp]|[Rr][Cc]|[Rr][Tt]'
const BN_INPUT_PATTERN = `^(\\d{9}|\\d{9}\\s*(${CRA_PROGRAM_IDENTIFIERS_INPUT})\\s*\\d{4})$`

export function validateBN(bn: string): boolean {
  const normalised = bn.replace(/\s/g, '').toUpperCase()
  return new RegExp(`^\\d{9}((${CRA_PROGRAM_IDENTIFIERS})\\d{4})?$`).test(normalised)
}

// ACRA Unique Entity Number: 9-char (8 digits + letter) or 10-char
// (S|T|F|R + year + entity type + serial + check letter).
// https://www.uen.gov.sg/
const UEN_VALIDATION_REGEX = /^(\d{8}[A-Z]|[STFR]\d{2}[A-Z]{2}\d{4}[A-Z])$/i

export function validateUEN(uen: string): boolean {
  return UEN_VALIDATION_REGEX.test(uen.replace(/\s/g, '').toUpperCase())
}

// Hong Kong Business Registration Number is 8 digits, optionally with a
// 3-digit branch suffix (xxxxxxxx or xxxxxxxx-xxx).
const BRN_VALIDATION_REGEX = /^\d{8}(-?\d{3})?$/

export function validateBRN(brn: string): boolean {
  return BRN_VALIDATION_REGEX.test(brn.replace(/\s/g, ''))
}

// Irish CRO company numbers are 1–7 digits.
// https://core.cro.ie/
const IRELAND_CRN_VALIDATION_REGEX = /^\d{1,7}$/

export function validateIrelandCRN(crn: string): boolean {
  return IRELAND_CRN_VALIDATION_REGEX.test(crn.replace(/\s/g, ''))
}

export function validateBusinessIdentifier(regionCode: string, businessIdentifier: string) {
  switch (regionCode) {
    case 'AU':
      return validateABN(businessIdentifier)
    case 'NZ':
      return validateNZBN(businessIdentifier)
    case 'GB':
      return validateCRN(businessIdentifier)
    case 'US':
      return validateEIN(businessIdentifier)
    case 'CA':
      return validateBN(businessIdentifier)
    case 'SG':
      return validateUEN(businessIdentifier)
    case 'HK':
      return validateBRN(businessIdentifier)
    case 'IE':
      return validateIrelandCRN(businessIdentifier)
    default:
      return false
  }
}

export function getAgencyCodeInfoFromCountryCode(countryCode: string): { label: string, lookupUrl: string } {
  switch (countryCode) {
    case 'AU':
      return { label: 'ABN', lookupUrl: 'https://abr.business.gov.au/' }
    case 'NZ':
      return { label: 'NZBN', lookupUrl: 'https://www.nzbn.govt.nz/' }
    case 'GB':
      return { label: 'CRN', lookupUrl: 'https://find-and-update.company-information.service.gov.uk/' }
    case 'US':
      return { label: 'EIN', lookupUrl: '' }
    case 'CA':
      return { label: 'BN', lookupUrl: 'https://www.canada.ca/en/revenue-agency/services/e-services/e-services-businesses/confirming-a-gst-hst-account-number.html' }
    case 'SG':
      return { label: 'UEN', lookupUrl: 'https://www.uen.gov.sg/' }
    case 'HK':
      return { label: 'BRN', lookupUrl: 'https://www.gov.hk/en/residents/taxes/etax/services/brn_enquiry.htm' }
    case 'IE':
      return { label: 'CRN', lookupUrl: 'https://core.cro.ie/' }
    default:
      return { label: '', lookupUrl: '' }
  }
}

export type AgencyCodeInputConstraints = {
  pattern: string
  minLength: number
  maxLength: number
  type: 'text' | 'number'
}

export function getAgencyCodeInputConstraintsFromCountryCode(countryCode: string): AgencyCodeInputConstraints {
  switch (countryCode) {
    case 'AU':
      return {
        pattern: '^(\\d *?){11}$',
        minLength: 11,
        maxLength: 11,
        type: 'number',
      }
    case 'NZ':
      return {
        pattern: '^(\\d *?){13}$',
        minLength: 13,
        maxLength: 13,
        type: 'number',
      }
    case 'GB':
      return {
        pattern: CRN_VALIDATION_REGEX.source,
        minLength: 8,
        maxLength: 8,
        type: 'text',
      }
    case 'US':
      return {
        pattern: '^(\\d *?){9}$',
        minLength: 9,
        maxLength: 9,
        type: 'number',
      }
    case 'CA':
      return {
        pattern: BN_INPUT_PATTERN,
        minLength: 9,
        maxLength: 20,
        type: 'text',
      }
    case 'SG':
      return {
        pattern: UEN_VALIDATION_REGEX.source,
        minLength: 9,
        maxLength: 10,
        type: 'text',
      }
    case 'HK':
      return {
        pattern: BRN_VALIDATION_REGEX.source,
        minLength: 8,
        maxLength: 12,
        type: 'text',
      }
    case 'IE':
      return {
        pattern: IRELAND_CRN_VALIDATION_REGEX.source,
        minLength: 1,
        maxLength: 7,
        type: 'number',
      }
    default:
      return {
        pattern: '',
        minLength: 0,
        maxLength: 50,
        type: 'text',
      }
  }
}
